import {
  Box, Typography, TextField, MenuItem, Select, FormControl, InputLabel,
  Checkbox, FormControlLabel, Radio, RadioGroup, Button, Paper, FormGroup,
  Chip,
} from '@mui/material'
import { LeadFormField, FieldType } from '../../lib/api/leadFormsApi'
import { themeColors } from '../../app/layout/theme'

interface FormPreviewProps {
  formName:        string
  description?:    string | null
  successMessage:  string
  fields:          LeadFormField[]
  mode?:           'desktop' | 'mobile'
  formCompanyName?: string | null
  formLogoUrl?:  string | null
}

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    '& fieldset': { borderColor: '#d0c9e8' },
    '&:hover fieldset': { borderColor: '#9b87c8' },
    '&.Mui-focused fieldset': { borderColor: themeColors.primary, borderWidth: '1.5px' },
  },
  '& .MuiInputBase-input': { fontSize: '14px', color: '#1a1a2e' },
  '& .MuiInputLabel-root': { fontSize: '14px' },
}

function PreviewField({ field }: { field: LeadFormField }) {
  const label = `${field.label}${field.required ? ' *' : ''}`
  const opts = field.configuration?.options ?? []

  switch (field.fieldType as FieldType) {
    case 'textarea':
      return (
        <TextField
          label={label}
          multiline
          rows={3}
          placeholder={field.placeholder ?? ''}
          fullWidth
          size="small"
          sx={inputSx}
          InputProps={{ readOnly: true }}
        />
      )
    case 'dropdown':
    case 'multi_select':
      return (
        <FormControl fullWidth size="small" sx={inputSx}>
          <InputLabel sx={{ fontSize: '14px' }}>{label}</InputLabel>
          <Select label={label} value="" sx={{ fontSize: '14px' }}>
            {opts.map((o, i) => <MenuItem key={i} value={o.value} sx={{ fontSize: '14px' }}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
      )
    case 'radio':
      return (
        <Box>
          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#444', mb: 0.5 }}>{label}</Typography>
          <RadioGroup>
            {opts.map((o, i) => (
              <FormControlLabel
                key={i}
                value={o.value}
                control={<Radio size="small" sx={{ color: themeColors.primary, '&.Mui-checked': { color: themeColors.primary } }} />}
                label={<Typography sx={{ fontSize: '13px' }}>{o.label}</Typography>}
              />
            ))}
          </RadioGroup>
        </Box>
      )
    case 'checkbox':
      return (
        <Box>
          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#444', mb: 0.5 }}>{label}</Typography>
          <FormGroup>
            {opts.map((o, i) => (
              <FormControlLabel
                key={i}
                control={<Checkbox size="small" sx={{ color: themeColors.primary, '&.Mui-checked': { color: themeColors.primary } }} />}
                label={<Typography sx={{ fontSize: '13px' }}>{o.label}</Typography>}
              />
            ))}
          </FormGroup>
        </Box>
      )
    case 'date':
      return (
        <TextField
          label={label}
          type="date"
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={inputSx}
          InputProps={{ readOnly: true }}
        />
      )
    default:
      return (
        <TextField
          label={label}
          placeholder={field.placeholder ?? ''}
          fullWidth
          size="small"
          type={field.fieldType === 'email' ? 'email' : field.fieldType === 'number' ? 'number' : field.fieldType === 'url' ? 'url' : 'text'}
          sx={inputSx}
          InputProps={{ readOnly: true }}
        />
      )
  }
}

export const FormPreview = ({ formName, description, fields, mode = 'desktop', formCompanyName, formLogoUrl }: FormPreviewProps) => {
  const visibleFields = fields.filter(f => f.isVisible)

  return (
    <Box
      sx={{
        width: mode === 'mobile' ? 375 : '100%',
        mx: 'auto',
        minHeight: 400,
        bgcolor: '#f4f2fa',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        p: 3,
        borderRadius: '12px',
        border: `1px solid ${themeColors.border}`,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: mode === 'mobile' ? 340 : 520,
          borderRadius: '12px',
          overflow: 'hidden',
          border: `1px solid #e0d9f5`,
          boxShadow: '0 4px 20px rgba(75,48,130,0.12)',
        }}
      >
        {/* Header band */}
        <Box sx={{ background: themeColors.primaryGradient, px: 3, py: 2.5 }}>
          {(formLogoUrl || formCompanyName) ? (
            /* Branding layout: logo on left, [company name / form title] stacked on right */
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {formLogoUrl && (
                <Box
                  component="img"
                  src={formLogoUrl}
                  alt="Company logo"
                  sx={{
                    width: 48, height: 48, borderRadius: '10px',
                    objectFit: 'contain',
                    bgcolor: 'rgba(255,255,255,0.15)',
                    p: '5px',
                    flexShrink: 0,
                  }}
                />
              )}
              <Box>
                {formCompanyName && (
                  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#fff', lineHeight: 1.2, letterSpacing: '0.1px' }}>
                    {formCompanyName}
                  </Typography>
                )}
                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'rgba(255,255,255,0.72)', lineHeight: 1.4, mt: formCompanyName ? 0.25 : 0 }}>
                  {formName || 'Form Name'}
                </Typography>
                {description && (
                  <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.4, mt: 0.25 }}>
                    {description}
                  </Typography>
                )}
              </Box>
            </Box>
          ) : (
            /* No branding — show form title prominently */
            <>
              <Typography sx={{ fontSize: mode === 'mobile' ? '16px' : '20px', fontWeight: 700, color: '#fff', mb: 0.5 }}>
                {formName || 'Form Name'}
              </Typography>
              {description && (
                <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.82)', lineHeight: 1.5 }}>
                  {description}
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Fields */}
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, bgcolor: '#fff' }}>
          {visibleFields.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ fontSize: '13px', color: themeColors.text.muted }}>
                Enable fields on the left to see them here
              </Typography>
            </Box>
          ) : (
            visibleFields.map(f => <PreviewField key={f.id} field={f} />)
          )}

          {visibleFields.length > 0 && (
            <Button
              variant="contained"
              fullWidth
              sx={{
                mt: 1,
                background: themeColors.primaryGradient,
                borderRadius: '8px',
                py: 1.25,
                fontSize: '14px',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(75,48,130,0.35)',
                '&:hover': { background: themeColors.primaryDark },
              }}
            >
              Submit
            </Button>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ px: 3, py: 1.5, bgcolor: '#faf9fe', borderTop: `1px solid #ede8f8`, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '11px', color: themeColors.text.muted }}>
            Powered by{' '}
            <Box component="span" sx={{ color: themeColors.primary, fontWeight: 600 }}>Momentum CRM</Box>
          </Typography>
        </Box>
      </Paper>
    </Box>
  )
}
